import React from "react"

import { Button, Collapse, Group, Slider, Stack, Switch, Text } from "@mantine/core"
import { IconBulb } from "@tabler/icons-react"

import ModuleSettingsCard from "../module-settings-card/ModuleSettingsCard"
import useLightModuleSettings from "./useLightModuleSettings"
import { ModuleSettingsProps } from "../module-settings-card/ModuleSettingsProps"
import { ModuleFC } from "../module-settings-card/ModuleFC"
import _DeviceLightsModuleSettings from "./_DeviceLightsModuleSettings"
import _GroupLightModuleSettings from "./_GroupLightModuleSettings"
import SettingsOverwriteWarning from "../settings-overwrite-warning/SettingsOverwriteWarning"
import { useLanguage } from "../../../../contexts/language-context"

type LightModuleSettingsProps = ModuleSettingsProps<"brightness">

const LightModuleSettings: ModuleFC<LightModuleSettingsProps> = Object.assign(
    ({ attrs, organisation, group, device }) => {
        const { form, onSubmit, reset, inheritSettings, processing } = useLightModuleSettings(
            attrs,
            organisation,
            group,
            device,
        )
        const { L } = useLanguage()

        return (
            <ModuleSettingsCard title={L("Lighting settings")} icon={IconBulb}>
                <form onSubmit={form.onSubmit(onSubmit)}>
                    <Stack spacing="xl">
                        <SettingsOverwriteWarning attrs={attrs} group={group} device={device} />
                        <Switch
                            label={L("Lights on/off")}
                            description={L("Toggle the device's lights and change the brightness.")}
                            checked={form.values.on}
                            onChange={(event) => {
                                const checked = event.currentTarget.checked;
                                form.setFieldValue("on", checked);
                                form.setFieldValue("brightness", checked ? 20 : 0);
                            }}
                        />
                        <Collapse ml="3.375rem" in={form.values.on}>
                            <Stack spacing={0}>
                                <Text size="sm">{L("Brightness")}</Text>
                                <Text size="xs" color="dimmed">
                                    {L("Determine how bright the lights are. Higher brightness uses more energy.")}
                                </Text>
                                <Slider
                                    mt="xs"
                                    step={5}
                                    label={(value: number | null) => (value ?? 0) + "%"}
                                    disabled={!attrs?.brightness.mutable}
                                    {...form.getInputProps("brightness")}
                                />
                            </Stack>
                        </Collapse>
                        <Group position="right">
                            {device && (
                                <Button
                                    variant="subtle"
                                    onClick={inheritSettings}
                                    disabled={form.values.brightness === null}
                                >
                                    {L("Apply group settings")}
                                </Button>
                            )}
                            <Button variant="subtle" onClick={reset} disabled={!form.isDirty()}>
                                {L("Reset")}
                            </Button>
                            <Button type="submit" disabled={!form.isDirty()} loading={processing}>
                                {L("Save")}
                            </Button>
                        </Group>
                    </Stack>
                </form>
            </ModuleSettingsCard>
        )
    },
    {
        Device: _DeviceLightsModuleSettings,
        Group: _GroupLightModuleSettings
    }
)

export default LightModuleSettings
